import type { inferRouterOutputs } from '@trpc/server'
import { createContext, type ReactNode, useContext, useMemo } from 'react'
import type { AppRouter } from '@/api/_trpc/router'
import { trpc } from '@/lib/trpc'

type RouterOutputs = inferRouterOutputs<AppRouter>

/**
 * Live overview payload. NOTE: this repo has no tRPC date transformer, so every
 * `Date` in the service DTO is serialized to an ISO-8601 STRING on the wire —
 * `sprintStartsOn`, `endsOn`, `targetDate` (and insight `generatedAt`) arrive as
 * `string`. Treat them as strings; wrap in `new Date(...)` only to format.
 */
export type OverviewData =
  RouterOutputs['admin']['coachingV2']['overview']['get']
export type OverviewInsight =
  RouterOutputs['admin']['coachingV2']['overview']['listInsights'][number]

export interface AdminOverviewValue {
  /** Undefined while loading or when the query errored / has no engagement. */
  overview: OverviewData | undefined
  insights: OverviewInsight[]
  isLoading: boolean
  isError: boolean
}

const AdminOverviewContext = createContext<AdminOverviewValue | null>(null)

/**
 * Live overview data boundary keyed by `clientId` (mirrors
 * `AdminInterviewsProvider`). Fetches `overview.get` + `overview.listInsights`
 * and exposes them via `useAdminOverview()`. Mounted both around the shell
 * header/stat-strip and inside the Overview tab; React Query dedupes the shared
 * query keys so both mounts resolve from a single request.
 */
export function AdminOverviewProvider({
  clientId,
  children,
}: {
  clientId: string
  children: ReactNode
}): ReactNode {
  const overviewQuery = trpc.admin.coachingV2.overview.get.useQuery({
    clientId,
  })
  const insightsQuery = trpc.admin.coachingV2.overview.listInsights.useQuery({
    clientId,
  })

  const value = useMemo<AdminOverviewValue>(
    () => ({
      overview: overviewQuery.data,
      insights: insightsQuery.data ?? [],
      isLoading: overviewQuery.isLoading || insightsQuery.isLoading,
      isError: overviewQuery.isError || insightsQuery.isError,
    }),
    [
      overviewQuery.data,
      overviewQuery.isLoading,
      overviewQuery.isError,
      insightsQuery.data,
      insightsQuery.isLoading,
      insightsQuery.isError,
    ],
  )

  return (
    <AdminOverviewContext.Provider value={value}>
      {children}
    </AdminOverviewContext.Provider>
  )
}

export function useAdminOverview(): AdminOverviewValue {
  const ctx = useContext(AdminOverviewContext)
  if (!ctx) {
    throw new Error(
      'useAdminOverview must be used within AdminOverviewProvider',
    )
  }
  return ctx
}
